import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ConfirmModal, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { TotvsSyncTask } from '../../domain/entities/totvs.entity';
import {
  ErrorText,
  formatDateTime,
  getApiErrorMessage,
  Mono,
  TotvsStatusBadge,
  TotvsStrategyBadge,
} from '../display';
import {
  useReprocessBatchTotvsSyncs,
  useTotvsSyncTasks,
} from '../hooks/use-totvs-sync-tasks';
import {
  countActiveTotvsSyncFilters,
  EMPTY_TOTVS_SYNC_FILTERS,
  TotvsSyncTasksFiltersModal,
  type TotvsSyncTasksFilters,
} from '../components/TotvsSyncTasksFiltersModal';
import { InfoTooltip } from '@/shared/components/info-tooltip';
import { useCanWriteTotvs } from '../hooks/use-totvs-permissions';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

const columns: ColumnDef<TotvsSyncTask>[] = [
  {
    key: 'status',
    header: 'Status',
    render: (row) => <TotvsStatusBadge status={row.status} />,
  },
  {
    key: 'offerName',
    header: 'Oferta',
    render: (row) => (
      <span className="text-(--color-text-primary) text-sm">
        {row.offerName}
      </span>
    ),
  },
  {
    key: 'externalIds',
    header: 'Produto/oferta',
    render: (row) => (
      <div className="space-y-1">
        <div>
          <span className="text-(--color-text-secondary) text-xs">
            Produto:{' '}
          </span>
          <Mono>{row.productExternalId}</Mono>
        </div>
        <div>
          <span className="text-(--color-text-secondary) text-xs">
            Oferta:{' '}
          </span>
          <Mono>{row.offerExternalId}</Mono>
        </div>
      </div>
    ),
  },
  {
    key: 'strategy',
    header: 'Estratégia',
    render: (row) => (
      <TotvsStrategyBadge
        strategy={row.strategy}
        worknowPhase={row.worknowPhase}
      />
    ),
  },
  {
    key: 'failedAttempts',
    header: 'Tentativas',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {row.failedAttempts}
      </span>
    ),
  },
  {
    key: 'updatedAt',
    header: 'Atualizado em',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {formatDateTime(row.updatedAt)}
      </span>
    ),
  },
];

export function TotvsSyncTasksPage() {
  const navigate = useNavigate();
  const canWrite = useCanWriteTotvs();
  const [searchParams, setSearchParams] = useSearchParams();
  // Página e filtros vivem na URL (não em useState) para sobreviver a
  // "Voltar" do detalhe do job — ver primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md.
  // Aplicar/limpar filtro também reseta a página; as duas mudanças precisam
  // ir num único setSearchParams (ver aviso em use-page-query-param.ts).
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, EMPTY_TOTVS_SYNC_FILTERS);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };
  const setFiltersAndResetPage = (next: TotvsSyncTasksFilters) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_TOTVS_SYNC_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [batchResult, setBatchResult] = useState<string | null>(null);

  const reprocessBatch = useReprocessBatchTotvsSyncs();

  const { data, isLoading, refetch, isFetching } = useTotvsSyncTasks({
    page,
    perPage: PER_PAGE,
    status: filters.status || undefined,
    productExternalId: filters.productExternalId || undefined,
    errorCode: filters.errorCode || undefined,
    userId: filters.contactUserId || undefined,
  });

  const tasks = useMemo(() => data?.items ?? [], [data?.items]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const activeFilterCount = countActiveTotvsSyncFilters(filters);

  // O reprocesso em lote do backend só aceita status ou errorCode como filtro
  // — contato não é suportado, então desabilita o botão sempre que estiver
  // aplicado (mesmo combinado com status/errorCode), pra não reenfileirar
  // além do escopo que a listagem filtrada sugere.
  const canBatch =
    (!!filters.status || !!filters.errorCode) && !filters.contactUserId;

  const batchInfoMessage = filters.contactUserId
    ? "O reprocesso em lote não suporta filtro de contato — reenfileiraria TODOS os jobs do status/código de erro, não só os deste contato. Remova o filtro de contato em 'Filtros' para habilitar o botão."
    : "Reenfileira em massa os jobs que casam um filtro de status ou de código de erro (até 500 por rodada). Aplique um desses filtros em 'Filtros' para habilitar o botão.";

  const handleApplyFilters = (next: TotvsSyncTasksFilters) => {
    setFiltersAndResetPage(next);
    setBatchResult(null);
  };

  const handleClearFilters = () => {
    setFiltersAndResetPage(EMPTY_TOTVS_SYNC_FILTERS);
    setBatchResult(null);
  };

  const handleConfirmBatch = () => {
    setBatchResult(null);
    reprocessBatch.mutate(
      {
        status: filters.status || undefined,
        errorCode: filters.errorCode || undefined,
        reason: 'Reprocesso em lote pelo admin TOTVS',
      },
      {
        onSuccess: (result) => {
          setIsBatchOpen(false);
          setBatchResult(
            `${result.reprocessed} job(s) reenfileirado(s)${
              result.capped
                ? ' — havia mais que o teto por rodada; repita para continuar.'
                : '.'
            }`,
          );
        },
      },
    );
  };

  const batchScope = [
    filters.status ? `status "${filters.status}"` : null,
    filters.errorCode ? `erro "${filters.errorCode}"` : null,
  ]
    .filter(Boolean)
    .join(' e ');

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            TOTVS · Jobs de matrícula
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Acompanhe a fila de sincronização com o RM, encontre jobs travados
            ou com dados inválidos e reprocesse quando necessário. Clique num
            job para ver a timeline de tentativas e o payload.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="secondary"
                disabled={!canBatch || reprocessBatch.isPending}
                onClick={() => setIsBatchOpen(true)}
              >
                Reprocessar lote
              </Button>
              {!canBatch && (
                <InfoTooltip
                  label="Por que 'Reprocessar lote' está desabilitado"
                  message={batchInfoMessage}
                />
              )}
            </div>
          )}
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition"
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      <div className="space-y-1 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
        <p>
          Só compras de ofertas com a tag <Mono>[TV]</Mono> no nome viram job —
          as demais seguem pelo fluxo normal do Delivery. Vale para qualquer
          gateway (Guru e Hotmart).
        </p>
        <p>
          Job com o motivo <Mono>retido_para_liberacao_manual</Mono> nasceu fora
          da fila: o gateway dele está retido enquanto a virada do middleware
          não fecha. Ele só chega no RM se alguém abrir o job e mandar
          reprocessar.
        </p>
      </div>

      <TotvsSyncTasksFiltersModal
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <ConfirmModal
        open={isBatchOpen}
        onClose={() => setIsBatchOpen(false)}
        onConfirm={handleConfirmBatch}
        title="Reprocessar jobs em lote"
        description={`Reenfileirar (voltar para PENDENTE e zerar tentativas) todos os jobs que casam ${
          batchScope || 'o filtro atual'
        }? Jobs em processamento são ignorados.`}
        confirmLabel="Reprocessar lote"
        loading={reprocessBatch.isPending}
        errorMessage={
          reprocessBatch.error
            ? getApiErrorMessage(reprocessBatch.error, 'Falha na operação')
            : null
        }
      />

      <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
        {activeFilterCount > 0 && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-(--color-text-secondary)">
              {activeFilterCount === 1
                ? '1 filtro aplicado'
                : `${activeFilterCount} filtros aplicados`}
            </p>
            <button
              onClick={handleClearFilters}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {batchResult && (
          <p className="text-(--color-text-secondary) text-sm">{batchResult}</p>
        )}
        {reprocessBatch.error && !isBatchOpen && (
          <ErrorText>
            {getApiErrorMessage(
              reprocessBatch.error,
              'Falha ao reprocessar em lote',
            )}
          </ErrorText>
        )}

        <Table
          columns={columns}
          data={tasks}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="Nenhum job TOTVS encontrado."
          striped
          onRowClick={(row) => void navigate(`/totvs/jobs/${row.id}`)}
        />

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data
              ? `${data.total} ${data.total === 1 ? 'job encontrado' : 'jobs encontrados'}`
              : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
